<template>
    <aside v-if="open" class="vp-panel vp-transcript" noautohide :aria-label="t('Transcript')">
        <div class="vp-transcript-header">
            <span class="vp-label">{{ t("Transcript") }}</span>
            <button type="button" class="vp-icon-button" :aria-label="t('Close transcript')" @click="emit('close')">
                <VideoIcon name="close" :size="18" />
            </button>
        </div>

        <p v-if="loading" class="vp-transcript-empty">{{ t("Loading transcript…") }}</p>
        <p v-else-if="cues.length === 0" class="vp-transcript-empty">{{ t("No transcript available") }}</p>
        <ol v-else ref="listEl" class="vp-transcript-list">
            <li v-for="(cue, index) in cues" :key="index">
                <span v-if="speakerChanged(index)" class="vp-label vp-transcript-speaker">{{ cue.speaker }}</span>
                <button
                    type="button"
                    class="vp-transcript-line"
                    :aria-current="index === activeIndex ? 'true' : undefined"
                    @click="emit('seek', cue.start)"
                >
                    <span class="vp-mono vp-transcript-time">{{ formatClock(cue.start) }}</span>
                    <span class="vp-transcript-text">{{ cue.text }}</span>
                </button>
            </li>
        </ol>
    </aside>
</template>

<script setup>
import { nextTick, ref, watch } from "vue"
import VideoIcon from "./VideoIcon.vue"
import { formatClock } from "../format.js"
import { useCoreI18n } from "../useCoreI18n.js"

const props = defineProps({
    open: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    cues: { type: Array, default: () => [] },
    activeIndex: { type: Number, default: -1 },
})

const emit = defineEmits(["seek", "close"])

const t = useCoreI18n()
const listEl = ref(null)

function speakerChanged(index) {
    const speaker = props.cues[index]?.speaker

    return !!speaker && speaker !== props.cues[index - 1]?.speaker
}

watch(
    () => [props.activeIndex, props.open],
    async () => {
        if (!props.open) {
            return
        }

        await nextTick()
        listEl.value?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest", behavior: "smooth" })
    },
)
</script>

<style>
.vp-transcript {
    position: absolute;
    z-index: 2;
    top: 84px;
    right: 16px;
    bottom: 112px;
    width: min(380px, calc(100% - 32px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.vp-transcript-header {
    padding: 4px 4px 4px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.vp-transcript-empty {
    margin: 0;
    padding: 16px;
    font-size: 13px;
    color: var(--vp-muted);
}

.vp-transcript-list {
    margin: 0;
    padding: 0 8px 8px;
    overflow-y: auto;
    list-style: none;
}

.vp-transcript-line {
    width: 100%;
    padding: 8px;
    display: flex;
    gap: 12px;
    border-radius: 10px;
    text-align: left;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(251, 249, 245, 0.6);
    background: transparent;
}

.vp-transcript-line:hover {
    background: rgba(251, 249, 245, 0.08);
}

.vp-transcript-line[aria-current="true"] {
    color: var(--vp-paper);
    box-shadow: inset 2px 0 0 var(--vp-accent);
}

.vp-transcript-speaker {
    display: block;
    padding: 12px 8px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.vp-transcript-list > li:first-child .vp-transcript-speaker {
    padding-top: 4px;
}

.vp-transcript-time {
    flex-shrink: 0;
    padding-top: 2px;
    font-size: 12px;
    color: var(--vp-muted);
}

@media (max-height: 420px) {
    .vp-transcript {
        top: 8px;
        bottom: 8px;
    }
}
</style>
